import React from 'react';
import { useEditor } from '../../context/EditorContext';
import { ContextMenuSub, ContextMenuSubContent, ContextMenuSubTrigger, ContextMenuItem } from '../ui/context-menu';
import { Cloud, FileText, SendHorizontal } from 'lucide-react';
import { toast } from 'sonner';

/**
 * "Copy to Publication ▸" — sends the selected objects (or a page) straight into another open publication (document
 * tab) without switching to it. The target keeps an undo step; the toast offers to jump there.
 * kind: 'objects' → COPY_OBJECTS_TO_TAB { ids }, 'page' → COPY_PAGE_TO_TAB { index }.
 */
export const CopyToPublicationItems = ({ kind = 'objects', ids, pageIndex, itemClass = '', testIdPrefix = 'copy-to-publication' }) => {
  const { state, dispatch, rawDispatch } = useEditor();
  const others = (state.docTabs || []).filter((t) => t.id !== state.activeTabId && t.doc);
  const label = kind === 'page' ? 'Copy Page to Publication' : 'Copy to Publication';
  const send = (tab) => {
    const name = tab.doc?.projectName || 'Untitled Publication';
    if (kind === 'page') dispatch({ type: 'COPY_PAGE_TO_TAB', payload: { tabId: tab.id, index: pageIndex ?? state.currentPageIndex } });
    else dispatch({ type: 'COPY_OBJECTS_TO_TAB', payload: { tabId: tab.id, ids: ids || state.selectedObjectIds } });
    const what = kind === 'page' ? 'Page copied' : `${(ids || state.selectedObjectIds).length === 1 ? 'Object' : `${(ids || state.selectedObjectIds).length} objects`} copied`;
    toast.success(`${what} to “${name}”`, {
      id: 'copy-to-publication',
      description: kind === 'page' ? 'Added after its current page — it has an undo step there.' : 'Placed on its current page — it has an undo step there.',
      action: { label: 'Go there', onClick: () => rawDispatch({ type: 'SWITCH_DOC_TAB', payload: { id: tab.id } }) },
      duration: 6000,
    });
  };
  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger className={itemClass} disabled={!others.length} data-testid={`${testIdPrefix}-trigger`}>
        <SendHorizontal className="w-4 h-4 text-muted-foreground" /> {label}
        {!others.length && <span className="ml-auto pl-3 text-[11px] text-muted-foreground">no other open publication</span>}
      </ContextMenuSubTrigger>
      {others.length > 0 && (
        <ContextMenuSubContent className="w-64 z-[95]" data-testid={`${testIdPrefix}-list`}>
          {others.map((t) => (
            <ContextMenuItem key={t.id} className={itemClass} onClick={() => send(t)} data-testid={`${testIdPrefix}-tab-${t.id}`}>
              {t.doc?.projectId ? <Cloud className="w-4 h-4 text-primary" /> : <FileText className="w-4 h-4 text-muted-foreground" />}
              <span className="truncate">{t.doc?.projectName || 'Untitled Publication'}</span>
              <span className="ml-auto pl-3 text-[11px] text-muted-foreground shrink-0">{(t.doc?.pages || []).length}p</span>
            </ContextMenuItem>
          ))}
        </ContextMenuSubContent>
      )}
    </ContextMenuSub>
  );
};
